import { css } from '@emotion/react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AdaptoActions } from '@frontend/adapto-actions';
import { ActionsUI } from '@frontend/contact-actions';
import { FirstWordBoldText } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { Photos } from '@frontend/photos';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { ListRow, Text } from '@frontend/design-system';
import { checkIsNonAttendedEventStatus } from '../../../components/calendar-view/utils';
import { useGetAppointmentDetails } from '../../../hooks';
import { getFullName } from '../../../utils';
import { getTimeString } from '../../../utils/date-helpers';
import { AppointmentBadgeStatus } from './AppointmentBadgeStatus';

type PatientListRowProps = {
  appt?: Appointment;
  optOutText?: boolean;
  locationId: string;
  shouldShowAppointmentModal?: boolean;
  appointments?: Appointment[];
};

export const PatientListRow = ({
  optOutText,
  appt,
  locationId,
  shouldShowAppointmentModal = false,
  appointments,
}: PatientListRowProps) => {
  const { t } = useTranslation('schedule-pulse');
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  const start = getTimeString(appt?.start);
  const duration = appt?.duration ? `(${appt.duration} Min)` : '';

  const { triggerProps: viewAppointmentTriggerProps, Modal: AppointmentDetailsModal } = useGetAppointmentDetails({
    appointmentId: appt?.id || '',
    locationId: locationId || appt?.locationId || '',
    appointments,
    enabled: shouldShowAppointmentModal,
  });

  const { triggerProps: callTriggerProps, Modal: PhoneCallModal } = ActionsUI.actions.usePhoneCallAction({
    context: {
      personId: appt?.person?.personId ?? '',
      phoneNumber: appt?.person?.mobilePhone ?? '',
      locationId: appt?.locationId,
    },
  });

  const handleOpenAppointmentModal = () => {
    if (shouldShowAppointmentModal) viewAppointmentTriggerProps?.onClick();
  };

  const handleSetPersonIdAndLocationId = (appointment?: Appointment) => {
    if (appointment?.person?.personId && appointment?.locationId) {
      const personId = appointment.person.personId;
      const locationId = appointment.locationId;
      setPersonId(personId, true, locationId);
    }
  };

  const gridTemplateAreas = `
  "name name name name"
  "time time ${appt?.practitionerName ? 'practitioner practitioner' : 'time time'}"
  "type type ${appt?.operatory ? 'operatory operatory' : 'type type'}"
`;

  const isNonAttendedV3Event = isScheduleV3FlagEnabled
    ? checkIsNonAttendedEventStatus(appt?.statusOfficeView as EventStatus)
    : false;

  return (
    <>
      {AppointmentDetailsModal}
      <ListRow css={{ border: 0, padding: theme.spacing(1, 2) }} onClick={handleOpenAppointmentModal}>
        <ListRow.Lead css={{ alignItems: 'start', paddingRight: theme.spacing(1) }}>
          <Photos.ContactProfilePhoto
            size='small'
            personId={(appt?.person && appt.person?.personId) ?? ''}
            firstName={appt?.person?.firstName}
            lastName={appt?.person?.lastName}
            onClick={() => handleSetPersonIdAndLocationId(appt)}
          />
        </ListRow.Lead>
        <ListRow.Content
          css={{
            display: 'grid',
            gridTemplateAreas: gridTemplateAreas,
            gridTemplateColumns: 'repeat(4, 1fr)',
            gridTemplateRows: 'repeat(3, auto)',
            gap: theme.spacing(0.5),
          }}
        >
          <FirstWordBoldText css={{ textAlign: 'left', gridArea: 'name' }}>
            {getFullName(appt?.person) || t('Unknown')}
          </FirstWordBoldText>
          <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'time' }]}>
            {start && duration ? `${start}${duration ? ` ${duration}` : ''}` : '-'}
          </Text>
          {appt?.practitionerName && (
            <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'practitioner' }]}>{appt?.practitionerName}</Text>
          )}
          <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'type' }]}>{appt?.type ?? '-'}</Text>
          {appt?.operatory && (
            <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'operatory' }]}>{appt?.operatory}</Text>
          )}
        </ListRow.Content>
        <ListRow.Trail
          css={{
            justifySelf: 'end',
            gap: theme.spacing(1),
            paddingLeft: theme.spacing(0.5),
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <AppointmentBadgeStatus status={appt?.statusOfficeView ?? ''} appointmentId={appt?.id ?? ''} iconOnly />
          {optOutText && (
            <AdaptoActions size='small' iconColor='subdued' variant='action-buttons'>
              <AdaptoActions.Action
                icon='phone'
                hoverLabel='Click to Call'
                label={t('Click to Call')}
                color='icon'
                onClick={() => callTriggerProps?.onClick()}
              />
            </AdaptoActions>
          )}
        </ListRow.Trail>
      </ListRow>
      {PhoneCallModal}
    </>
  );
};

const textStyles = (isNonAttendedV3Event: boolean) => css`
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${theme.colors.neutral70};
  font-size: ${theme.fontSize(12)};
  text-decoration: ${isNonAttendedV3Event ? 'line-through' : 'none'};
`;
